import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { Navigation } from '../customer/Navigation'
import OnboardingTour from './OnboardingTour'

const navItems = [
  { to: '/company', labelKey: 'nav.companyOverview' },
  { to: '/company/transactions', labelKey: 'nav.companyTransactions' },
  { to: '/company/payouts', labelKey: 'nav.companyPayouts' },
  { to: '/company/internal-receipts', labelKey: 'nav.companyInternalReceipts' },
  { to: '/company/reports', labelKey: 'nav.companyReports' },
  { to: '/company/settings', labelKey: 'nav.companySettings' },
]

/**
 * Company panel layout (/company/*).
 * Uses the same top navigation as the rest of the site, then company sidebar + content.
 * Colors match admin and main site (white/gray/blue).
 */
export default function CompanyDashboardLayout() {
  const { t } = useTranslation('portal')
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const isActive = (to: string) =>
    location.pathname === to || (to !== '/company' && location.pathname.startsWith(to))
  const linkClass = (to: string) =>
    isActive(to) ? 'font-medium text-blue-600' : 'text-gray-600 hover:text-gray-900'

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <OnboardingTour />
      <div className="flex flex-1 pt-16 relative">
        {/* Mobile backdrop when sidebar is open */}
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          className={`md:hidden fixed inset-0 bg-black/40 z-10 transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setSidebarOpen(false)}
        />
        {/* Company sidebar: drawer on mobile, static on md+ */}
        <aside
          className={`
            w-52 border-r bg-white flex flex-col shrink-0
            fixed inset-y-0 left-0 pt-16 z-20 transform transition-transform duration-200 ease-out
            md:static md:pt-0 md:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <div className="p-4 flex items-center justify-between border-b md:border-b-0">
            <span className="font-medium text-gray-900">{t('nav.companyDashboard')}</span>
            <button
              type="button"
              className="md:hidden p-2 -m-2 text-gray-500 hover:text-gray-900"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-4 flex flex-col gap-1 overflow-y-auto">
            {navItems.map(({ to, labelKey }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`block py-2 px-2 rounded-lg ${linkClass(to)}`}
              >
                {t(labelKey)}
              </Link>
            ))}
          </nav>
        </aside>
        {/* Main content + mobile menu button (same pattern as admin: bar only on mobile) */}
        <main className="flex-1 min-w-0 flex flex-col bg-gray-50">
          <div className="flex items-center gap-2 p-3 border-b border-gray-200 bg-white md:hidden">
            <button
              type="button"
              className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('nav.openMenu')}
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-700">{t('nav.menu')}</span>
          </div>
          <div className="flex-1 p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
